import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  Loader2,
  LogOut,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  Wallet,
  X,
} from "lucide-react";
import { ManualPayments, tokenStorage } from "@/lib/api";

const STATUS_LABEL = {
  pending: { text: "Pending", cls: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  approved: { text: "Approved", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" },
  rejected: { text: "Rejected", cls: "bg-rose-500/15 text-rose-200 border-rose-500/30" },
};

const KIND_LABEL = {
  eligibility: "Loan Eligibility",
  partner: "Partner Joining Fee",
  investment: "Investor Investment",
};

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function AdminManualPayments() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [viewProof, setViewProof] = useState(null); // payment_id
  const [reviewing, setReviewing] = useState(null); // {payment, action}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ManualPayments.list(filter);
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const doReview = async (payment, action, note) => {
    try {
      await ManualPayments.review(payment.id, { action, admin_note: note || "" });
      setNotice({
        ok: true,
        text:
          action === "approve"
            ? `Payment ${payment.id.slice(0, 8)} approved. ${KIND_LABEL[payment.kind]} auto-activated.`
            : `Payment ${payment.id.slice(0, 8)} rejected. Applicant notified.`,
      });
      setReviewing(null);
      await load();
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const totals = items.reduce(
    (acc, p) => {
      acc.count += 1;
      acc.value += p.amount || 0;
      return acc;
    },
    { count: 0, value: 0 },
  );

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/leads"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F0D788]"
            >
              <ArrowLeft size={12} /> Back to Admin
            </Link>
            <div className="w-px h-6 bg-white/10 mx-2" />
            <h1 className="font-display text-base md:text-lg flex items-center gap-2">
              <Wallet size={16} className="text-[#F0D788]" /> Manual Payments
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <section className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <Metric label="In this view" value={totals.count} testId="mp-total" />
          <Metric label="Aggregate amount" value={`₹${inr(totals.value)}`} accent="text-[#F0D788]" testId="mp-amount" />
          <Metric label="Filter" value={filter.toUpperCase()} accent="text-emerald-300" testId="mp-filter" />
        </section>

        <div className="flex items-center gap-2 mb-4">
          {[
            ["pending", "Pending review"],
            ["approved", "Approved"],
            ["rejected", "Rejected"],
            ["all", "All"],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`mp-tab-${v}`}
              className={`px-3.5 py-1.5 rounded-full text-xs border transition ${
                filter === v
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F0D788]"
                  : "border-white/10 text-white/60 hover:border-white/25"
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            data-testid="mp-refresh"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {notice && (
          <div
            data-testid="mp-notice"
            className={`mb-6 rounded-xl border px-4 py-3 text-xs flex items-center gap-3 ${
              notice.ok
                ? "bg-emerald-400/10 border-emerald-400/35 text-emerald-100"
                : "bg-rose-400/10 border-rose-400/35 text-rose-100"
            }`}
          >
            {notice.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {notice.text}
            <button onClick={() => setNotice(null)} className="ml-auto opacity-70 underline">
              Dismiss
            </button>
          </div>
        )}

        <section className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Payer</th>
                  <th className="text-left px-4 py-3">For</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">UPI ref</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-white/55">
                      No {filter} manual payments.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((p, i) => {
                    const st = STATUS_LABEL[p.status] || STATUS_LABEL.pending;
                    return (
                      <tr
                        key={p.id}
                        data-testid={`mp-row-${i}`}
                        className="border-t border-white/5"
                      >
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{p.payer_name}</div>
                          <div className="text-[11px] text-white/50">{p.payer_email}</div>
                          <div className="text-[11px] text-white/40">{p.payer_phone}</div>
                          {p.recaptcha_status === "soft_failed" && (
                            <div
                              data-testid={`mp-recaptcha-flag-${i}`}
                              title={`reCAPTCHA soft-fail · ${p.recaptcha_detail || "no token"}`}
                              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border border-amber-400/50 bg-amber-400/10 text-amber-200 text-[10px] uppercase tracking-wider"
                            >
                              reCAPTCHA · review closely
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white/80">
                          {KIND_LABEL[p.kind] || p.kind}
                          <div className="text-[11px] text-white/40 font-mono">
                            {p.target_id?.slice(0, 8)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#F0D788] font-semibold">
                          ₹{inr(p.amount)}
                        </td>
                        <td className="px-4 py-3 text-xs text-white/70 font-mono">
                          {p.upi_reference || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${st.cls}`}
                          >
                            {st.text}
                          </span>
                          {p.admin_note && (
                            <div className="text-[10px] text-white/45 mt-1 max-w-[160px] truncate" title={p.admin_note}>
                              {p.admin_note}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-white/55">
                          {p.created_at?.slice(0, 16).replace("T", " ")}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => setViewProof(p)}
                            data-testid={`mp-view-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#D4AF37]/35 hover:border-[#D4AF37]/70 text-[11px] text-[#F0D788] mr-1.5"
                          >
                            <Eye size={11} /> Proof
                          </button>
                          {p.status === "pending" && (
                            <>
                              <button
                                onClick={() => setReviewing({ payment: p, action: "approve" })}
                                data-testid={`mp-approve-${i}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-400/40 hover:border-emerald-400/70 text-[11px] text-emerald-300 mr-1.5"
                              >
                                <ThumbsUp size={11} /> Approve
                              </button>
                              <button
                                onClick={() => setReviewing({ payment: p, action: "reject" })}
                                data-testid={`mp-reject-${i}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/40 hover:border-rose-400/70 text-[11px] text-rose-300"
                              >
                                <ThumbsDown size={11} /> Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {viewProof && <ProofModal payment={viewProof} onClose={() => setViewProof(null)} />}
      {reviewing && (
        <ReviewModal
          payment={reviewing.payment}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
          onSubmit={(note) => doReview(reviewing.payment, reviewing.action, note)}
        />
      )}
    </div>
  );
}

function Metric({ label, value, accent = "text-white", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-[#0A1F44] border border-[#D4AF37]/20 p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{label}</div>
      <div className={`mt-1.5 font-display text-2xl ${accent}`}>{value}</div>
    </div>
  );
}

function ProofModal({ payment, onClose }) {
  const proofUrl = ManualPayments.proofUrl(payment.id);
  const isPdf = (payment.proof_content_type || "").includes("pdf");
  const token = tokenStorage.get();
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(proofUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => live && setBlobUrl(URL.createObjectURL(b)))
      .catch(() => live && setErr(true));
    return () => {
      live = false;
    };
  }, [proofUrl, token]);

  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-[#0A1F44] rounded-3xl border border-[#D4AF37]/30 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">
            Payment proof · <span className="font-mono text-sm text-[#F0D788]">{payment.id.slice(0, 8)}</span>
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-xs mb-4">
          <Info label="Payer" value={payment.payer_name} />
          <Info label="Amount" value={`₹${inr(payment.amount)}`} />
          <Info label="Email" value={payment.payer_email} />
          <Info label="Phone" value={payment.payer_phone} />
          <Info label="UPI ref" value={payment.upi_reference || "—"} />
          <Info label="Service" value={KIND_LABEL[payment.kind] || payment.kind} />
        </div>
        <div className="rounded-2xl bg-black/40 border border-white/10 min-h-[300px] flex items-center justify-center p-2">
          {err ? (
            <div className="text-rose-200 text-sm">Could not load proof.</div>
          ) : !blobUrl ? (
            <Loader2 className="animate-spin text-white/40" size={20} />
          ) : isPdf ? (
            <embed src={blobUrl} type="application/pdf" className="w-full h-[500px]" />
          ) : (
            <img src={blobUrl} alt="proof" className="max-h-[500px]" />
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ payment, action, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isApprove = action === "approve";

  const submit = async (e) => {
    e.preventDefault();
    if (!isApprove && !note.trim()) return;
    setBusy(true);
    await onSubmit(note.trim());
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0A1F44] rounded-3xl border border-[#D4AF37]/30 p-7">
        <h3 className="font-display text-lg mb-2">
          {isApprove ? "Approve payment?" : "Reject payment"}
        </h3>
        <p className="text-white/60 text-xs mb-5">
          {isApprove
            ? `₹${inr(payment.amount)} from ${payment.payer_name} for ${KIND_LABEL[payment.kind]}. On approval, the service will be auto-activated and a confirmation email sent.`
            : `${payment.payer_name} will be emailed the rejection reason below.`}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/55 block mb-1.5">
              Admin note {isApprove ? "(optional)" : <span className="text-[#D4AF37]">*</span>}
            </span>
            <textarea
              data-testid="mp-review-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required={!isApprove}
              rows={3}
              placeholder={
                isApprove
                  ? "Optional — will be stored on the payment record."
                  : "Reason shown to the applicant, e.g. 'Payment reference not found in our records.'"
              }
              className="w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white placeholder:text-white/30"
            />
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="mp-review-confirm"
              className={`px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60 ${
                isApprove ? "btn-gold" : "bg-rose-500 hover:bg-rose-400 text-white"
              }`}
            >
              {busy ? "Working…" : isApprove ? "Confirm approval" : "Confirm rejection"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
      <div className="mt-1 text-white/85 text-sm break-words">{value}</div>
    </div>
  );
}
